/**
 * TMG Offline Engine
 * src/shared/services/offlineEngine.js
 *
 * Shared service layer for offline-first data capture.
 * Manages an IndexedDB Outbox for photos and text field changes.
 * Both the Disposal Portal and future Inventory screen plug into this engine.
 *
 * Architecture:
 *   - Capture (photo or text) → enqueue to Outbox → return immediately (no wait)
 *   - Sync → drain Outbox sequentially → upload to Azure → purge blobs on success
 *   - Status → subscribe to get live Outbox counts and per-item state
 *
 * Usage:
 *   import { offlineEngine } from '../shared/services/offlineEngine';
 *
 *   // Check connectivity
 *   offlineEngine.isOnline()
 *
 *   // Enqueue a photo
 *   await offlineEngine.enqueuePhoto({ toolId, disposalId, photoType, file, uploadedBy, siteId })
 *
 *   // Enqueue text field changes
 *   await offlineEngine.enqueueTextUpdate({ toolId, disposalId, fieldChanges })
 *
 *   // Trigger sync
 *   await offlineEngine.sync(onProgress)
 *
 *   // Subscribe to status changes
 *   const unsubscribe = offlineEngine.subscribe(status => { ... })
 */

import { apiFetch } from '../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';const DB_NAME      = 'tmg-offline';
const DB_VERSION   = 1;
const STORE_NAME   = 'outbox';

// ── IndexedDB helpers ────────────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'outbox_id' });
        store.createIndex('by_status',  'status',  { unique: false });
        store.createIndex('by_tool_id', 'tool_id', { unique: false });
      }
    };

    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

function dbPut(db, item) {
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.put(item);
    req.onsuccess = () => resolve();
    req.onerror   = (e) => reject(e.target.error);
  });
}

function dbGetAll(db) {
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.getAll();
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

function dbDelete(db, outboxId) {
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.delete(outboxId);
    req.onsuccess = () => resolve();
    req.onerror   = (e) => reject(e.target.error);
  });
}

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── HEIC / WebP → JPEG conversion ────────────────────────────────────────────

const NEEDS_CONVERSION = new Set(['image/heic', 'image/heif', 'image/webp']);
const CONVERT_EXTS     = new Set(['heic', 'heif', 'webp']);

/**
 * If the file is HEIC, HEIF, or WebP, convert to JPEG via canvas.
 * Returns the original file unchanged for all other formats.
 */
function ensureJpeg(file) {
  const ext  = file.name.split('.').pop().toLowerCase();
  const mime = (file.type || '').toLowerCase();
  if (!NEEDS_CONVERSION.has(mime) && !CONVERT_EXTS.has(ext)) return Promise.resolve(file);

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas  = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob(
          blob => {
            URL.revokeObjectURL(url);
            if (!blob) { reject(new Error('Canvas conversion failed')); return; }
            const newName = file.name.replace(/\.[^.]+$/, '.jpg');
            resolve(new File([blob], newName, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.92
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to decode image for conversion')); };
    img.src = url;
  });
}

// ── File → base64 ────────────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]); // strip data:...;base64,
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// ── Connectivity detection ───────────────────────────────────────────────────

function checkOnline() {
  return navigator.onLine;
}

// ── Offline Engine ───────────────────────────────────────────────────────────

class OfflineEngine {
  constructor() {
    this._db          = null;
    this._subscribers = new Set();
    this._syncing     = false;
    this._mode        = 'auto'; // 'auto' | 'offline'

    // Listen for connectivity changes
    window.addEventListener('online',  () => this._notifySubscribers());
    window.addEventListener('offline', () => this._notifySubscribers());
  }

  // ── Initialise ─────────────────────────────────────────────────────────────

  async init() {
    if (this._db) return;
    this._db = await openDB();
  }

  async _ensureDB() {
    if (!this._db) await this.init();
    return this._db;
  }

  // ── Mode & connectivity ────────────────────────────────────────────────────

  /**
   * Returns true if uploads should go immediately (online + not in manual offline mode)
   */
  isOnline() {
    return checkOnline() && this._mode !== 'offline';
  }

  /**
   * Manually set offline mode.
   * 'auto'    = follow navigator.onLine
   * 'offline' = always queue to Outbox regardless of connectivity
   */
  setMode(mode) {
    if (!['auto', 'offline'].includes(mode)) throw new Error('mode must be auto or offline');
    this._mode = mode;
    this._notifySubscribers();
  }

  getMode() {
    return this._mode;
  }

  // ── Outbox status ──────────────────────────────────────────────────────────

  /**
   * Returns current Outbox status snapshot.
   * {
   *   mode: 'auto' | 'offline',
   *   online: bool,
   *   pending: number,
   *   failed: number,
   *   syncing: bool,
   *   items: [...outbox items with blob_data stripped for lightness]
   * }
   */
  async getStatus() {
    const db    = await this._ensureDB();
    const items = await dbGetAll(db);
    const pending = items.filter(i => i.status === 'pending').length;
    const failed  = items.filter(i => i.status === 'failed').length;

    return {
      mode:    this._mode,
      online:  checkOnline(),
      pending,
      failed,
      syncing: this._syncing,
      // Strip blob data from status snapshot to keep it lightweight
      items: items.map(i => ({ ...i, blob_data: i.blob_data ? '[blob]' : null })),
    };
  }

  /**
   * Subscribe to status changes.
   * Returns an unsubscribe function.
   *
   * const unsubscribe = offlineEngine.subscribe(status => setOutboxStatus(status))
   * // later: unsubscribe()
   */
  subscribe(callback) {
    this._subscribers.add(callback);
    // Immediately call with current status
    this.getStatus().then(callback);
    return () => this._subscribers.delete(callback);
  }

  async _notifySubscribers() {
    if (this._subscribers.size === 0) return;
    const status = await this.getStatus();
    this._subscribers.forEach(cb => cb(status));
  }

  // ── Enqueue ────────────────────────────────────────────────────────────────

  /**
   * Enqueue a photo for upload.
   *
   * If online (and not in offline mode), uploads immediately and returns the
   * Media document from the server. If offline, queues to Outbox and returns
   * a placeholder receipt so the UI can update immediately.
   *
   * @param {Object} params
   * @param {string} params.toolId       - internal Cosmos tool ID
   * @param {string} params.disposalId   - disposal document ID (optional for inventory)
   * @param {string} params.photoType    - 'bol' | 'weigh_ticket' | 'tool_photo' | 'other'
   * @param {File}   params.file         - File object from input or camera
   * @param {string} params.uploadedBy   - user identifier
   * @param {string} params.siteId       - site_id from the tool record
   *
   * @returns {Promise<{ queued: bool, outbox_id?: string, media_id?: string, blob_url?: string }>}
   */
  async enqueuePhoto({ toolId, disposalId, photoType, file, uploadedBy, siteId, callerEmail = null }) {
    const db = await this._ensureDB();

    // Convert HEIC/HEIF/WebP → JPEG before upload
    const converted = await ensureJpeg(file);
    const fileExt   = converted.name.split('.').pop().toLowerCase();
    const base64    = await fileToBase64(converted);
    const outboxId = generateId();

    if (this.isOnline()) {
      // Online path — upload immediately
      try {
        const result = await this._uploadPhoto({
          toolId, disposalId, photoType, fileData: base64,
          fileExt, uploadedBy, siteId, callerEmail
        });
        await this._notifySubscribers();
        return { queued: false, ...result };
      } catch (err) {
        // Upload failed even though online — fall through to queue
        console.warn('offlineEngine: online upload failed, queuing instead:', err.message);
      }
    }

    // Offline path — queue to Outbox
    const item = {
      outbox_id:   outboxId,
      type:        'photo',
      tool_id:     toolId,
      disposal_id: disposalId || null,
      photo_type:  photoType,
      file_ext:    fileExt,
      uploaded_by: uploadedBy,
      site_id:     siteId,
      caller_email: callerEmail || null,   // per-op author; replayed to the gate (mirrors text_update)
      blob_data:   base64,        // stored temporarily — purged after successful upload
      status:      'pending',
      retry_count: 0,
      created_at:  new Date().toISOString(),
      error:       null,
    };

    await dbPut(db, item);
    await this._notifySubscribers();

    return {
      queued:     true,
      outbox_id:  outboxId,
      photo_type: photoType,
      queued_at:  item.created_at,
    };
  }

  /**
   * Enqueue text field changes for a disposal or tool record.
   *
   * If online, patches immediately. If offline, queues to Outbox.
   *
   * @param {Object} params
   * @param {string} params.toolId        - internal Cosmos tool ID
   * @param {string} params.disposalId    - disposal document ID (for disposal writes)
   * @param {string} params.target        - 'disposal' | 'tool'
   * @param {Object} params.fieldChanges  - camelCase field changes e.g. { truckNbr: 'TRK-001' }
   *
   * @returns {Promise<{ queued: bool, outbox_id?: string }>}
   */
  async enqueueTextUpdate({ toolId, disposalId, target = 'disposal', fieldChanges, callerEmail = null }) {
    const db = await this._ensureDB();

    if (this.isOnline()) {
      // Online path — write immediately
      try {
        await this._writeTextUpdate({ toolId, disposalId, target, fieldChanges, callerEmail });
        await this._notifySubscribers();
        return { queued: false };
      } catch (err) {
        console.warn('offlineEngine: online text write failed, queuing instead:', err.message);
      }
    }

    // Offline path — queue to Outbox
    const outboxId = generateId();
    const item = {
      outbox_id:     outboxId,
      type:          'text_update',
      tool_id:       toolId,
      disposal_id:   disposalId || null,
      target:        target,
      field_changes: fieldChanges,
      caller_email:  callerEmail || null,   // per-op author (the editor); replayed to the gate
      status:        'pending',
      retry_count:   0,
      created_at:    new Date().toISOString(),
      error:         null,
    };

    await dbPut(db, item);
    await this._notifySubscribers();

    return { queued: true, outbox_id: outboxId };
  }

  // ── Sync ───────────────────────────────────────────────────────────────────

  /**
   * Drain the Outbox — upload all pending items sequentially.
   *
   * @param {Function} onProgress - called after each item with current status snapshot
   *   onProgress({ outbox_id, type, status, pending, failed, total })
   *
   * @returns {Promise<{ uploaded: number, failed: number }>}
   */
  async sync(onProgress) {
    if (this._syncing) {
      console.warn('offlineEngine: sync already in progress');
      return { uploaded: 0, failed: 0 };
    }

    if (!checkOnline()) {
      throw new Error('Cannot sync — device is offline');
    }

    const db    = await this._ensureDB();
    const items = await dbGetAll(db);
    const pending = items.filter(i => i.status === 'pending' || i.status === 'failed');

    if (pending.length === 0) return { uploaded: 0, failed: 0 };

    this._syncing = true;
    await this._notifySubscribers();

    let uploaded = 0;
    let failed   = 0;

    for (const item of pending) {
      // Mark as uploading
      item.status = 'uploading';
      await dbPut(db, item);
      await this._notifySubscribers();

      try {
        if (item.type === 'photo') {
          const uploadResult = await this._uploadPhoto({
            toolId:      item.tool_id,
            disposalId:  item.disposal_id,
            photoType:   item.photo_type,
            fileData:    item.blob_data,
            fileExt:     item.file_ext,
            uploadedBy:  item.uploaded_by,
            siteId:      item.site_id,
            callerEmail: item.caller_email || null,
          });
          // Success — delete entire item (blob purged with it)
          console.log(`offlineEngine: upload succeeded for ${item.outbox_id}, deleting from queue`, uploadResult);
          await dbDelete(db, item.outbox_id);
          uploaded++;
          await this._notifySubscribers();

        } else if (item.type === 'text_update') {
          await this._writeTextUpdate({
            toolId:       item.tool_id,
            disposalId:   item.disposal_id,
            target:       item.target,
            fieldChanges: item.field_changes,
            callerEmail:  item.caller_email || null,
          });
          // Success — delete item
          await dbDelete(db, item.outbox_id);
          uploaded++;
          await this._notifySubscribers();
        }

      } catch (err) {
        // Failure — mark failed, increment retry count, keep in Outbox
        item.retry_count = (item.retry_count || 0) + 1;
        item.status      = item.retry_count >= 3 ? 'failed' : 'pending';
        item.error       = err.message;
        await dbPut(db, item);
        failed++;
        await this._notifySubscribers();
        console.error(`offlineEngine: failed to sync item ${item.outbox_id}:`, err.message);
      }

      // Notify progress after each item
      if (onProgress) {
        const status = await this.getStatus();
        onProgress({
          outbox_id: item.outbox_id,
          type:      item.type,
          status:    item.status,
          pending:   status.pending,
          failed:    status.failed,
          total:     pending.length,
        });
      }
    }

    this._syncing = false;
    await this._notifySubscribers();

    return { uploaded, failed };
  }

  /**
   * Retry only failed items (leaves pending items alone).
   */
  async retryFailed(onProgress) {
    const db    = await this._ensureDB();
    const items = await dbGetAll(db);
    // Reset failed → pending so sync() picks them up
    for (const item of items.filter(i => i.status === 'failed')) {
      item.status = 'pending';
      await dbPut(db, item);
    }
    return this.sync(onProgress);
  }

  /**
   * Clear all failed items from the queue (permanent discard).
   */
  async clearFailed() {
    const db    = await this._ensureDB();
    const items = await dbGetAll(db);
    for (const item of items.filter(i => i.status === 'failed')) {
      await dbDelete(db, item.outbox_id);
    }
    await this._notifySubscribers();
  }

  /**
   * Returns true if there are pending or failed items in the Outbox.
   * Use this to warn the user before they close/navigate away.
   */
  async hasPendingItems() {
    const db    = await this._ensureDB();
    const items = await dbGetAll(db);
    return items.some(i => i.status === 'pending' || i.status === 'failed');
  }

  // ── Private: API calls ────────────────────────────────────────────────────

  async _uploadPhoto({ toolId, disposalId, photoType, fileData, fileExt, uploadedBy, siteId, callerEmail = null }) {
    const response = await apiFetch(`${API_BASE_URL}/upload_media`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tool_id:      toolId,
        disposal_id:  disposalId,
        photo_type:   photoType,
        file_data:    fileData,
        file_ext:     fileExt,
        uploaded_by:  uploadedBy,    // accepted-but-ignored by gated backend; kept for contract
        site_id:      siteId,        // accepted-but-ignored by gated backend; kept for contract
        caller_email: callerEmail,   // 2.6 media-auth — drives the air-gap on upload_media
      }),
    });

    console.log(`offlineEngine: upload_media response status=${response.status} ok=${response.ok}`);

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `upload_media failed: ${response.status}`);
    }

    // Backend returned 2xx — upload succeeded. Parse body but don't let
    // a JSON parse failure re-throw and mark the item as failed.
    try {
      return await response.json();
    } catch (parseErr) {
      console.warn('offlineEngine: upload succeeded (2xx) but response body parse failed:', parseErr.message);
      return { media_id: null, blob_url: null, uploaded_at: new Date().toISOString() };
    }
  }

  async _writeTextUpdate({ toolId, disposalId, target, fieldChanges, callerEmail = null }) {
    if (target === 'disposal') {
      const params   = new URLSearchParams({ disposal_id: disposalId });
      const response = await apiFetch(`${API_BASE_URL}/update_disposal?${params}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(fieldChanges),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `update_disposal failed: ${response.status}`);
      }
    } else if (target === 'tool') {
      const params   = new URLSearchParams({ tool_id: toolId });
      if (callerEmail) params.append('caller_email', callerEmail);
      const response = await apiFetch(`${API_BASE_URL}/update_tool?${params}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(fieldChanges),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `update_tool failed: ${response.status}`);
      }
    } else {
      throw new Error(`Unknown target: ${target}`);
    }
  }

  // ── Media fetch ───────────────────────────────────────────────────────────

  /**
   * Fetch all uploaded media for a tool (from Cosmos via get_media Function).
   * Returns array of { media_id, photo_type, blob_url, uploaded_at, uploaded_by }
   */
  async getMedia(toolId, callerEmail = null) {
    const params = new URLSearchParams({ tool_id: toolId });
    if (callerEmail) params.append('caller_email', callerEmail);
    const response = await apiFetch(`${API_BASE_URL}/get_media?${params}`);
    if (!response.ok) throw new Error(`get_media failed: ${response.status}`);
    return response.json();
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

export const offlineEngine = new OfflineEngine();
export default offlineEngine;
